import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { MatTreeModule } from '@angular/material/tree';
import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import pt from '@angular/common/locales/pt';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { CoreModule } from './core/core.module';
import { HttpInterceptorService } from './commons/http-interceptor.service';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { FIREBASE_OPTIONS, AngularFireModule } from '@angular/fire/compat';
import { MatExpansionModule } from '@angular/material/expansion';
import { AngularFireAuthModule, USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { FullscreenModalComponent } from './core/fullscreen-modal/fullscreen-modal.component';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { FullscreenModalCameraComponent } from './core/fullscreen-modal-camera/fullscreen-modal-camera.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FullscreenModalContextualSearchComponent } from './core/fullscreen-modal-contextual-search/fullscreen-modal-contextual-search.component';
import { UploadListaComprasComponent } from './core/upload-lista-compras/upload-lista-compras.component';
import { FileUpload } from 'primeng/fileupload';
import { ModalsComponent as ModalsFavoritesComponent } from './pages/minha-conta/favorites/modals/modals.component';
import { TranslocoModule, TRANSLOCO_CONFIG, TRANSLOCO_LOADER, translocoConfig } from '@ngneat/transloco';
import { HttpLoader } from './http-loader';
import { SharedModule } from './pages/shared/shared.module';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { MessageService } from 'primeng/api';
import { TieredMenuModule } from 'primeng/tieredmenu';   // ou MenubarModule

registerLocaleData(pt);

@NgModule({
  declarations: [
    AppComponent,
    FullscreenModalContextualSearchComponent,
    FullscreenModalCameraComponent,
    FullscreenModalComponent,
    ModalsFavoritesComponent,
    UploadListaComprasComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    BrowserAnimationsModule,
    MatMenuModule,
    MatExpansionModule,
    MatDividerModule,
    MatTreeModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    SharedModule,
    CoreModule,
    FileUpload,
    TieredMenuModule,
    TranslocoModule,
    ZXingScannerModule,
    RouterModule.forRoot([]),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Keep AngularFireModule for compatibility, if needed
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
  ],
  providers: [
    MessageService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    { provide: LOCALE_ID, useValue: 'pt-br' },
    { provide: FIREBASE_OPTIONS, useValue: environment.firebaseConfig },
    // Defensiva: o AngularFireAuth (compat), usado pelo AuthService, não tinha
    // wiring de emulador — em offline ele autenticava direto no Firebase real
    // de staging. Guardado por useEmulators (true só no environment default/offline;
    // ausente em staging e explicitamente false em prod) — em staging/prod o
    // valor é `undefined` e nada muda.
    {
      provide: USE_AUTH_EMULATOR,
      useValue: (environment as any).useEmulators ? [(environment as any).emulators.auth.url] : undefined,
    },
    {
      provide: TRANSLOCO_CONFIG,
      useValue: translocoConfig({
        availableLangs: ['pt-br', 'en-us', 'es-al', 'fr-fr', 'zh-cn', 'de-de'],
        defaultLang: 'pt-br',
        reRenderOnLangChange: true,
        prodMode: false,
      }),
    },
    { provide: TRANSLOCO_LOADER, useClass: HttpLoader },
    provideHttpClient(withInterceptorsFromDi()),
    provideFirebaseApp(() => {
      const app = initializeApp(environment.firebaseConfig);
      // Modo offline (config `offline`): conecta o Firestore ao emulador. Em
      // dev/staging/prod `useEmulators` é undefined -> comportamento inalterado.
      if ((environment as any).useEmulators) {
        const emu = (environment as any).emulators;
        // try/catch: a mesma instância [DEFAULT] também é conectada em
        // firebase.service.initApp(); a segunda chamada lança e é ignorada aqui.
        try {
          connectFirestoreEmulator(getFirestore(app), emu.firestore.host, emu.firestore.port);
        } catch (e) {
          console.warn('[firebase] Firestore emulator já conectado (app.module):', e);
        }
      }
      return app;
    }),
    provideAuth(() => {
      const auth = getAuth();
      if ((environment as any).useEmulators) {
        connectAuthEmulator(auth, (environment as any).emulators.auth.url, { disableWarnings: true });
      }
      return auth;
    }),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          darkModeSelector: false || 'none'
        }
      }
    })
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
